/**
 * Over-budget audits — the runs that tripped a per-source cost-breach rule.
 *
 * These are the same audits that fire the two inline Slack alerts into
 * `#ops-audit-cost-breach` (`"Audit Cost Breach"` / `"Audit Vendor+Infra Cost
 * Breach"`), evaluated against the same thresholds: the tables and the rule
 * live in `@a7y/types` (`audit-cost-thresholds.ts`), and both the alert path
 * and the BigQuery query behind this table read them from there.
 *
 * The alerts themselves are forward-only Slack posts with no persistence (see
 * `docs/audit-engine/ops-audit-cost-alert.md` §7 non-goals) — the `audit_cost_summary`
 * BQ row is the audit trail. So this section replays the rules over the window
 * rather than reading alert history, which also means it covers runs from
 * before the alerts existed.
 *
 * Two rules, reported per row as `kind`:
 *   infra          — infra > infra threshold for that source
 *   vendor_infra    — vendor > 0 and vendor + infra > combined threshold
 *   both           — the run tripped each rule (two Slack alerts fired)
 */
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ViewQueryButton } from './ViewQueryButton';
import { formatLocal, formatDuration, formatInt, formatPct, formatUsd } from './format';
import type { ApiEnvelope, CostBreachKind, CostBreachesData, OutlierDetailData } from './types';

/** Matches `BREACH_COLOR` in DailyTrendChart — same flag, same hue. */
const BREACH_COLOR = '#d03b3b';

const KIND_LABEL: Record<CostBreachKind, string> = {
  infra: 'infra',
  vendor_infra: 'vendor+infra',
  both: 'both',
};

const KIND_HINT: Record<CostBreachKind, string> = {
  infra: "Infra cost alone exceeded this source's infra threshold.",
  vendor_infra:
    "Vendor spend was non-zero and vendor + infra exceeded this source's combined threshold.",
  both: 'Tripped both rules — each posted its own Slack alert.',
};

/**
 * Full `audit_cost_summary` payload for one row, reusing the existing
 * outlier-detail endpoint rather than adding a second per-audit lookup.
 */
function BreachDetail({ auditId }: { auditId: string }) {
  const { data, isLoading, isError } = useQuery<ApiEnvelope<OutlierDetailData>>({
    queryKey: ['audit-spend', 'outlier-detail', auditId],
    queryFn: () => api.getAuditSpendOutlierDetail(auditId),
    staleTime: 60_000,
  });

  if (isLoading) return <div className="px-3 py-2 text-xs text-zinc-500">loading…</div>;
  if (isError) return <div className="px-3 py-2 text-xs text-red-500">failed to load detail</div>;
  if (!data?.data.found) {
    return (
      <div className="px-3 py-2 text-xs text-zinc-500">
        Detail not found — the outlier-detail lookup only reaches back 7 days, so older breaches in
        this window have no payload to show.
      </div>
    );
  }
  return (
    <pre className="px-3 py-2 max-h-72 overflow-auto text-[11px] font-mono text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950">
      {JSON.stringify(data.data.data, null, 2)}
    </pre>
  );
}

interface CostBreachesTableProps {
  envelope: ApiEnvelope<CostBreachesData> | undefined;
  loading: boolean;
  days: number;
}

export function CostBreachesTable({ envelope, loading, days }: CostBreachesTableProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const data = envelope?.data;
  const rows = data?.rows ?? [];
  const breachRate = data && data.audit_count > 0 ? data.total_breaches / data.audit_count : 0;

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 inline-flex items-center gap-1.5">
            <TriangleAlert className="h-4 w-4" style={{ color: BREACH_COLOR }} aria-hidden />
            Over-budget audits ({days}d)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Runs that crossed a per-source cost-breach threshold — the same rule that alerts into{' '}
            <code>#ops-audit-cost-breach</code>. Thresholds are shared with the alert path, so this
            list and Slack always agree.
          </p>
        </div>
        {data && (
          <div className="text-right text-xs text-zinc-500">
            <div
              className="text-base font-semibold"
              style={{ color: data.total_breaches > 0 ? BREACH_COLOR : undefined }}
            >
              {formatInt(data.total_breaches)}
            </div>
            <div>
              of {formatInt(data.audit_count)} audits ({formatPct(breachRate, 2)})
            </div>
          </div>
        )}
      </div>

      {data && data.by_source.length > 0 && (
        <div className="px-4 py-2 border-b border-zinc-200 dark:border-zinc-700 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
          {data.by_source.map((s) => (
            <span key={s.source ?? '(null)'} className="inline-flex items-center gap-1">
              <span className="font-medium text-zinc-700 dark:text-zinc-300">
                {s.source ?? '(null)'}
              </span>
              {formatInt(s.breach_count)}/{formatInt(s.audit_count)} • worst{' '}
              {formatUsd(s.max_breach_amount)} over
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-500">loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-500">
          No audits crossed a cost-breach threshold in the last {days} days.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 dark:bg-zinc-800/50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 w-6" />
                <th className="text-left px-3 py-2">audit_id</th>
                <th className="text-left px-3 py-2">source</th>
                <th className="text-left px-3 py-2">rule</th>
                <th className="text-right px-3 py-2">vendor</th>
                <th className="text-right px-3 py-2">infra</th>
                <th className="text-right px-3 py-2">combined</th>
                <th className="text-right px-3 py-2">threshold</th>
                <th className="text-right px-3 py-2">over by</th>
                <th className="text-right px-3 py-2">duration</th>
                <th className="text-left px-3 py-2">when</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = expanded === r.audit_id;
                // Show the threshold the row actually tripped. When both rules
                // fired, the combined one is the higher bar, so it's the more
                // informative number to surface.
                const shownThreshold =
                  r.kind === 'infra' ? r.infra_threshold : r.combined_threshold;
                return (
                  // Keyed on the Fragment, not the inner rows: a row and its
                  // expanded detail are one list item, so React needs the key
                  // on the wrapper it actually reconciles.
                  <Fragment key={r.audit_id}>
                    <tr
                      className="border-t border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 cursor-pointer"
                      onClick={() => setExpanded(open ? null : r.audit_id)}
                    >
                      <td className="px-3 py-2 text-zinc-500">
                        {open ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.audit_id}</td>
                      <td className="px-3 py-2 text-xs">{r.source ?? '(null)'}</td>
                      <td className="px-3 py-2 text-xs">
                        <span
                          title={KIND_HINT[r.kind]}
                          className="inline-block rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5"
                        >
                          {KIND_LABEL[r.kind]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-zinc-500 tabular-nums">
                        {formatUsd(r.vendor_cost)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-zinc-500 tabular-nums">
                        {formatUsd(r.infra_cost)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {formatUsd(r.combined_cost)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-zinc-500 tabular-nums">
                        {formatUsd(shownThreshold)}
                      </td>
                      <td
                        className="px-3 py-2 text-right text-xs font-semibold tabular-nums"
                        style={{ color: BREACH_COLOR }}
                      >
                        +{formatUsd(r.breach_amount)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-zinc-500">
                        {formatDuration(r.duration_ms)}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                        {formatLocal(r.publish_time)}
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-t border-zinc-200 dark:border-zinc-700">
                        <td colSpan={11} className="bg-zinc-50 dark:bg-zinc-950">
                          <BreachDetail auditId={r.audit_id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-2 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center justify-between gap-2">
        {data?.truncated && (
          <span className="text-xs text-zinc-500">
            Showing the {formatInt(rows.length)} most recent of {formatInt(data.total_breaches)} —
            the per-source counts above cover the whole window.
          </span>
        )}
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
